import {
  translateLegacy,
  Supervisor,
  DomAdapter,
  emitElements,
  applyOps,
  diffMinimal,
  createClient,
  makeHandlerContext,
  dispatchEvent,
} from 'provident-ssr'

/**
 * Initializes the client-side Preempt application using the Providence engine.
 *
 * @useCase Main browser entry point bootstrapped by Vite.
 * @processFlow Reads #preempt-initial-data, runs translateLegacy, boots
 *   Supervisor, performs first diff/paint via DomAdapter. After first paint,
 *   mutations flow through clientAPI ops (never a full recompile).
 */
async function init() {
  try {
    const scriptEl = document.getElementById('preempt-initial-data')
    let data: any = null

    if (scriptEl?.textContent) {
      data = JSON.parse(scriptEl.textContent.trim())
    } else {
      document.querySelector<HTMLDivElement>('#app')!.innerHTML =
        '<div>Loading from backend...</div>'
      const prefersDark =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      const tagsQuery = prefersDark ? '?tags=dark-mode' : ''
      const res = await fetch(`/api/content/1${tagsQuery}`)
      if (!res.ok) throw new Error(`Server returned ${res.status}`)
      const raw = await res.json()
      // Normalise to the Providence envelope shape
      data = {
        template: raw.template ?? raw,
        content: Array.isArray(raw.content)
          ? raw.content
          : raw.content
          ? [raw.content]
          : [],
        userData: raw.userData ?? null,
      }
    }

    // Normalise doc.content to ContentPayload[] (D2/F5 migration trap)
    if (data.content && !Array.isArray(data.content)) {
      data.content = [data.content]
    }

    // Attach userData to every payload entry so handlers can read it
    const userData: any = data.userData ?? null
    if (Array.isArray(data.content)) {
      data.content = data.content.map((p: any) => ({
        ...p,
        userData: p.userData ?? userData,
      }))
    }

    const translated = translateLegacy(data)
    const hub = (translated.root as any).hubFor

    const supervisor = new Supervisor(hub ? { hub } : {})
    ;(supervisor as any).userData = userData

    for (const n of translated.nodes) supervisor.registerNode(n)

    for (const n of supervisor.allNodes()) {
      if (!n.destroyed) n.compilePath()
    }

    const clientAPI = createClient(supervisor)
    const handlerCtx = makeHandlerContext(supervisor, clientAPI)

    const mount = document.getElementById('app')!
    mount.innerHTML = ''
    const prevMap: Map<string, any> = new Map()

    const render = () => {
      // Collect template nodes (from translated.root)
      const templateNodes: any[] = []
      const tStack = [translated.root]
      while (tStack.length > 0) {
        const node = tStack.pop()!
        templateNodes.push(node)
        for (const child of node.children) tStack.push(child)
      }

      // Collect content nodes (from translated.content)
      const contentNodes: any[] = []
      for (const c of translated.content) {
        const cStack = [c]
        while (cStack.length > 0) {
          const node = cStack.pop()!
          contentNodes.push(node)
          for (const child of node.children) cStack.push(child)
        }
      }

      // Compile template and content nodes via compilePath
      const actionable: any[] = []
      for (const n of [...templateNodes, ...contentNodes]) {
        if (!n.destroyed) {
          actionable.push(...n.compilePath().actionable)
        }
      }

      const nodeMap = new Map(supervisor.allNodes().map((n) => [n.id, n]))
      const nextEls = emitElements(actionable, nodeMap as any)
      const ops = diffMinimal(prevMap, nextEls)
      applyOps(adapter, ops)
      prevMap.clear()
      for (const el of nextEls) prevMap.set(el.wire, el)
    }

    supervisor.runPhase('after-compile')

    const adapter = new DomAdapter(mount, {
      onEvent: (wire: string, domEvent: Event) => {
        let node = supervisor.getNode(wire)
        if (!node) {
          const el = prevMap.get(wire)
          const propId = (el?.props?.['prop:id'] || el?.props?.['css:id'] || el?.props?.['data:node-id']) as string
          if (propId && typeof propId === 'string') {
            const cleanId = propId.startsWith('preempt-node-') ? propId.slice('preempt-node-'.length) : propId
            node = supervisor.getNode(cleanId)
          }
        }
        if (!node && wire.includes(':')) {
          const parts = wire.split(':')
          for (let i = parts.length - 1; i >= 0; i--) {
            const candidate = parts[i]!
            if (candidate.startsWith('node-')) {
              node = supervisor.getNode(candidate)
              if (node) break
            }
          }
        }
        if (node) {
          const results = dispatchEvent(node, handlerCtx, domEvent.type, domEvent)
          render()
          if (Array.isArray(results)) {
            for (const r of results) {
              if (r && typeof (r as any).then === 'function') {
                ;(r as Promise<any>).then(() => render()).catch(() => render())
              }
            }
          }
        }
      },
    })

    // Initial render / first paint
    render()

    // Expose clientAPI for handlers and external mutations
    ;(window as any).Preempt = { supervisor, clientAPI, translateLegacy, render }
  } catch (err) {
    console.error('Error initializing Preempt:', err)
    document.querySelector<HTMLDivElement>('#app')!.innerHTML =
      `<div>Error loading: ${err}</div>`
  }
}

init()
